<template>
    <template v-if="!showVideoLayer">
        <div class="meta-isf--preview">
            <div v-if="adSubType === AdSubType.Video" class="meta-isf__play-overlay">
                <span class="meta-isf__play-overlay__button" @click="startVideoPlay">
                    <Icon type="md-play" />
                </span>
            </div>

            <div class="meta-isf__banner">
                <div class="meta-isf__preview">
                    <div class="meta-isf__preview__top">
                        <span class="meta-isf__disc" />
                        <span class="meta-isf__disc" />
                    </div>

                    <div class="meta-isf__preview__bottom">
                        <div class="meta-isf__preview__bottom__info">
                            <div class="meta-isf__preview__bottom__info__head">
                                <span class="meta-isf__disc" />

                                <div class="meta-isf__text">
                                    <span class="meta-isf__line" />
                                    <span class="meta-isf__line" />
                                </div>
                            </div>

                            <div class="meta-isf__text">
                                <span class="meta-isf__line" />
                                <span class="meta-isf__line" />
                            </div>

                            <div class="meta-isf__preview__bottom__info__actions">
                                <span class="meta-isf__blob" />
                                <span class="meta-isf__blob" />
                            </div>
                        </div>

                        <div class="meta-isf__preview__bottom__controls">
                            <span class="meta-isf__disc" />
                            <span class="meta-isf__disc" />
                            <span class="meta-isf__disc" />
                            <span class="meta-isf__disc" />
                        </div>
                    </div>
                </div>

                <div class="meta-isf__data">
                    <div class="meta-isf__data__banner-preview">
                        <slot />
                    </div>

                    <div class="meta-isf__data__info">
                        <span v-if="metaOverwrites.fbHeadline">
                            <strong>{{ metaOverwrites.fbHeadline }}</strong>
                        </span>
                        <span v-if="metaOverwrites.fbDisplayLink">{{ metaOverwrites.fbDisplayLink }} - Sponsored</span>
                    </div>

                    <div class="meta-isf__data__actions">
                        <Icon type="ios-close" />
                        <Icon type="ios-more" />
                    </div>
                </div>
            </div>
        </div>
    </template>

    <template v-else>
        <div class="meta-isf--playing">
            <div v-if="adSubType === AdSubType.Video" class="meta-isf__video-wrap">
                <div class="meta-isf__video">
                    <div class="meta-isf__video__info">Video will resume after the ad</div>
                    <div class="meta-isf__video__banner">
                        <slot />
                    </div>

                    <div class="meta-isf__video__actions">
                        <div>0:0{{ adCounter }} sponsored</div>
                        <button v-if="metaOverwrites?.fbCallToAction">{{ metaOverwrites.fbCallToAction }}</button>
                    </div>

                    <div class="meta-isf__video__description">
                        <div class="meta-isf__video__description__info">
                            <div class="meta-isf__video__description__info__avatar">
                                <Icon type="logo-facebook" />
                            </div>

                            <div class="meta-isf__video__description__info__text">
                                <a
                                    v-if="metaOverwrites?.fbDisplayLink"
                                    class="meta-isf__description-link"
                                    :href="safeUrl"
                                >
                                    {{ metaOverwrites.fbDisplayLink }}
                                </a>
                                <p v-if="metaOverwrites.fbHeadline">
                                    <strong v-if="metaOverwrites.fbHeadline">{{ metaOverwrites.fbHeadline }}</strong>
                                </p>
                                <div v-if="metaOverwrites?.fbPrimaryText" class="meta-isf__text">
                                    {{ metaOverwrites?.fbPrimaryText }}
                                </div>
                            </div>
                        </div>
                        <Icon class="meta-isf__video__description__icon" type="ios-more" />
                    </div>

                    <div class="meta-isf__video__progress-bar">
                        <div
                            class="meta-isf__video__progress-bar__fill"
                            :class="{ 'meta-isf__video__progress-bar__fill--animated': isProgressBarAnimated }"
                        />
                    </div>
                </div>
            </div>
        </div>
    </template>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { MasterTemplate } from "shared-types";

import { AdSubType } from "@/enums/assemble";
import type { MetaOverwrites } from "@/types/types";
import sanitizeUrl from "@/utils/sanitizeUrl";

export default defineComponent({
    name: "FacebookInStreamFull",
    props: {
        metaOverwrites: {
            type: Object as PropType<MetaOverwrites>,
            default: () => ({})
        },

        masterTemplate: {
            type: Object as PropType<MasterTemplate>
        },

        adSubType: {
            type: String as PropType<(typeof AdSubType)[keyof typeof AdSubType]>,
            default: AdSubType.Video
        },

        adSize: {
            type: Number
        }
    },

    emits: ["setInStreamValue"],

    data() {
        return {
            AdSubType,
            adCounter: 8,
            isProgressBarAnimated: false,
            showVideoLayer: false,
            countInterval: null as ReturnType<typeof setInterval> | null,
            safeUrl: ""
        };
    },
    watch: {
        adSubType() {
            this.$emit("setInStreamValue", false);
            if (this.adSubType === AdSubType.Video) {
                this.startVideoPlay();
            } else {
                this.resetAnimation();
            }
        }
    },

    mounted() {
        if (this.metaOverwrites.fbWebsiteUrl) {
            this.safeUrl = this.sanitizeUrl(this.metaOverwrites.fbWebsiteUrl);
        }
    },
    beforeUnmount() {
        this.resetAnimation();
    },
    methods: {
        startVideoPlay() {
            this.isProgressBarAnimated = true;
            this.showVideoLayer = true;
            this.$emit("setInStreamValue", true);
            this.countInterval = setInterval(() => {
                this.adCounter -= 1;
                if (this.adCounter === 0) {
                    this.resetAnimation();
                }
            }, 1000000);
        },
        resetAnimation() {
            if (this.countInterval !== null) {
                clearInterval(this.countInterval);
            }
            this.isProgressBarAnimated = false;
            this.showVideoLayer = false;
            this.adCounter = 8;
            this.$emit("setInStreamValue", false);
        },
        sanitizeUrl
    }
});
</script>

<style lang="scss">
.meta-isf--preview {
    width: 100%;
    background-color: #8b8d91;

    .meta-isf__play-overlay {
        width: 100%;
        height: 100%;

        position: absolute;
        left: 0;
        top: 0;

        display: flex;
        justify-content: center;
        align-items: center;

        .meta-isf__play-overlay__button {
            cursor: pointer;
            height: 75px;
            width: 75px;
            display: flex;
            border-radius: 50%;
            border: 3px solid #fff;
            background: #494a4c;
            align-items: center;
            justify-content: center;

            i {
                font-size: 42px;
                color: #fff;
            }
        }
    }

    .meta-isf__disc,
    .meta-isf__line,
    .meta-isf__blob {
        display: inline-block;
        background-color: #babbbd;
    }

    .meta-isf__disc {
        border-radius: 50%;

        width: 40px;
        height: 40px;
    }

    .meta-isf__line {
        border-radius: 5px;

        height: 8px;
    }

    .meta-isf__blob {
        border-radius: 35px;

        height: 50px;
        width: 50%;
    }

    .meta-isf__text {
        flex: 1;

        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 5px;
    }

    .meta-isf__banner {
        height: 100%;

        display: flex;
        flex-direction: column;
    }

    .meta-isf__preview {
        flex: 1;

        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .meta-isf__preview__top {
            padding: 10px;

            display: flex;
            gap: 10px;
            justify-content: flex-end;
        }

        .meta-isf__preview__bottom {
            padding: 10px;

            display: flex;
            gap: 20px;

            .meta-isf__preview__bottom__info {
                flex: 1;

                display: flex;
                flex-direction: column;
                gap: 10px;

                .meta-isf__preview__bottom__info__head {
                    display: flex;
                    gap: 10px;

                    .meta-isf__disc {
                        flex: 0 0 auto;
                    }

                    .meta-isf__text {
                        .meta-isf__line {
                            width: 45%;

                            &:last-child {
                                width: 30%;
                            }
                        }
                    }
                }

                .meta-isf__preview__bottom__info__actions {
                    display: flex;
                    gap: 15px;
                }
            }

            .meta-isf__preview__bottom__controls {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }
        }
    }

    .meta-isf__data {
        background: #444648;
        border-radius: 10px;

        overflow: hidden;
        margin: 10px;
        height: 75px;

        display: flex;
        justify-content: space-between;
        gap: 10px;

        .meta-isf__data__banner-preview {
            flex: 0 0 75px;
        }

        .meta-isf__data__info {
            flex: 1;
            padding: 6px 0;

            display: flex;
            flex-direction: column;
            justify-content: center;

            color: #fff;
        }

        .meta-isf__data__actions {
            padding: 6px 10px;

            display: flex;
            flex-direction: column;
            justify-content: space-between;

            i {
                color: #fff;
                font-weight: 600;
            }
        }
    }
}

.meta-isf--playing {
    .meta-isf__video-wrap {
        background-color: #000;
        height: 100%;

        .meta-isf__video {
            height: 100%;

            display: flex;
            flex-direction: column;

            .meta-isf__video__info {
                position: absolute;
                top: 20px;
                right: 0;
                width: 100%;
                z-index: 2;

                padding: 5px 10px;
                text-align: end;

                color: #fff;
                font-weight: 600;
            }

            .meta-isf__video__banner {
                flex: 1;
                overflow: hidden;
                display: flex;
            }

            .meta-isf__video__actions {
                display: flex;
                align-items: center;
                justify-content: space-between;

                color: #fff;
                padding: 10px;

                button {
                    color: #fff;
                    background: #191919;
                    font-weight: 600;
                    padding: 5px 10px;

                    border: 0;
                    border-radius: 5px;
                }
            }

            .meta-isf__video__description {
                display: flex;
                align-items: center;
                justify-content: space-between;

                margin: 10px;
                padding: 10px;

                border-radius: 20px;
                background: #282a2b;
                color: #fff;

                .meta-isf__video__description__info {
                    display: flex;
                    gap: 10px;

                    .meta-isf__video__description__info__avatar {
                        width: 60px;
                        height: 60px;
                        border-radius: 50%;

                        background: #ccc;

                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }

                    .meta-isf__video__description__info__text {
                        .meta-isf__description-link {
                            color: #fff;
                        }

                        .meta-isv__text {
                            padding: 0;
                            font-size: 14px;
                            margin-bottom: 0;
                        }

                        padding-right: 0;
                        font-weight: 600;
                    }
                }

                .meta-isf__video__description__icon {
                    font-size: 24px;
                    font-weight: 600;
                }
            }

            .meta-isf__video__progress-bar {
                height: 4px;
                width: 100%;
                background: #000;

                .meta-isf__video__progress-bar__fill {
                    height: 100%;
                    background: #d76a1c;
                    width: 0;
                }

                .meta-isf__video__progress-bar__fill--animated {
                    animation: progressAnimation 8s linear infinite;
                }
            }
        }
    }
}

@keyframes progressAnimation {
    0% {
        width: 0%;
    }
    100% {
        width: 100%;
    }
}
</style>
